import React, { useReducer, useCallback, useContext, useEffect, useState, useImperativeHandle } from 'react'
import _ from 'lodash'

type ContextProps = {
    fields: Array<any>,
    addField: (name: string, value: string | number | null, readOnly?: boolean, selectionMode?: 'single' | 'multi' | 'multicsv' | 'range' | 'singlerange', valueExpr?: string) => void,
    removeField: (name: string) => void,
    setFieldValue: (name: string, value: string | number | null, fromSelf?: boolean, force?: boolean) => void,
    getFieldValue: (name: string, fullValue?: boolean) => any,
    getFieldReadOnly: (name: string) => boolean,
    getFieldSelectionMode: (name: string) => 'single' | 'multi'  | 'multicsv' | 'range' | 'singlerange',
    addFieldChangeCallback: (field: string, cb: (v: any) => void, fromSelf?: boolean, from?: string) => void,
    removeFieldChangeCallback: (field: string, cb: (v: any) => void, from?: string) => void,
    onFieldValueNotFound: (field: string) => void,
    fieldHasCallbacks: (field: string) => boolean,
}

const IxLookupContext = React.createContext<ContextProps>({} as any)

const initialState: any = {}
const reducer = (state: any, action: any) => {
    switch (action.type) {
        case 'addField': {
            if (action.payload.name in state) { return state }
            const newState = {
                ...state,
                [action.payload.name]: {
                    value: action.payload.value,
                    readOnly: action.payload.readOnly !== undefined ? action.payload.readOnly : false,
                    selectionMode: action.payload.selectionMode,
                    valueExpr: action.payload.valueExpr || 'HDR_ID',
                }
            }
            return newState
        }
        case 'updateField':
            if (!(action.payload.name in state)) { return state }
            const r = {
                ...state,
                [action.payload.name]: {
                    value: action.payload.value,
                    readOnly: state[action.payload.name].readOnly,
                    selectionMode: state[action.payload.name].selectionMode,
                    valueExpr: state[action.payload.name].valueExpr
                }
            }
            return r
        case 'removeField':
            delete state[action.payload]
            return state
        /* v8 ignore next 2 */
        case 'resetState':
            const newState = Object.keys(state).reduce((acc: any, key) => {
                acc[key] = { ...state[key], value: null }
                return acc
            }, {})
            return newState
        default:
            throw new Error()
    }
}
//
// action creator helpers
const _addField = (data: any) => ({
    type: 'addField',
    payload: {
        name: data.name,
        value: data.value,
        readOnly: data.readOnly,
        selectionMode: data.selectionMode,
        valueExpr: data.valueExpr,
    }
})
const _removeField = (name: string) => ({
    type: 'removeField',
    payload: name
})
const updateField = (data: any) => ({
    type: 'updateField',
    payload: {
        name: data.name,
        value: data.value,
    }
})
const _resetState = () => ({
    type: 'resetState'
})

const callbacksInitialState: any = {}
const callbacksReducer = (state: any, action: any) => {
    switch (action.type) {
        case 'addCallback': {
            const callbacks = state[action.payload.field] || []
            const res = {
                ...state,
                [action.payload.field]: [...callbacks, action.payload.cbObj]
            }
            return res
        }
        case 'removeCallback': {
            const callbacks = state[action.payload.field] || []
            if (callbacks.length === 0) { return state }
            const res = {
                ...state,
                [action.payload.field]: callbacks.filter((cbObj: any) => cbObj.callback !== action.payload.callback)
            }
            return res
        }
        case 'removeField': {
            delete state[action.payload]
            return state
        }
        /* v8 ignore next 2 */
        default:
            throw new Error('')
    }
}

// action creator helpers
const addCallback = ({ field, cbObj }: any) => ({
    type: 'addCallback',
    payload: {
        field,
        cbObj
    }
})
const removeCallback = ({ field, callback }: any) => ({
    type: 'removeCallback',
    payload: {
        field,
        callback
    }
})
const removeFieldCallbacks = (name: string) => ({
    type: 'removeField',
    payload: name
})


const IxLookupProvider = React.forwardRef((props: any, ref) => {
    const { onFieldChanged, onFieldValueNotFound } = props
    const [fieldsState, dispatch] = useReducer(reducer, initialState)
    const [callbacksState, callbacksDispatch] = useReducer(callbacksReducer, callbacksInitialState)
    const [hasCallbacksState, setHasCallbacksState] = useState<string[]>([])

    const addField = useCallback((name: string, value: string | number | null, readOnly: boolean=false, selectionMode: 'single' | 'multi' | 'multicsv' | 'range' | 'singlerange'='single', valueExpr='HDR_ID') => {
        // check if field already exists
        dispatch(_addField({ name, value, readOnly, selectionMode, valueExpr }))
    }, [])

    const setFieldValue = useCallback((name: string, value: string | number | null, fromSelf = false, force = false) => {
        const payload = {
            name,
            value,
        }

        if (!fromSelf) {}

        dispatch(updateField(payload))
        onFieldChanged && onFieldChanged(name, value)

        const callbacks = callbacksState[name] || []
        callbacks.forEach((cbObj: any) => {
            if ((!fromSelf || !cbObj.self) && typeof(cbObj.callback) === 'function') {
                if (cbObj.self) {
                    cbObj.callback(value, force)
                } else {
                    cbObj.callback(value)
                }
            }
        })
    }, [callbacksState, onFieldChanged ])

    const removeField = useCallback((name: string) => {
        dispatch(_removeField(name))
        callbacksDispatch(removeFieldCallbacks(name))
    }, [])

    const getFieldValue = useCallback((name: string, fullValue=true) => {
        if (!(name in fieldsState)) { return undefined }

        if (fullValue) {
            return fieldsState[name].value
        } else {
            return typeof(fieldsState[name].value) === 'object' && fieldsState[name].value !== null ? fieldsState[name].value[fieldsState[name].valueExpr] : undefined
        }
    }, [fieldsState])

    const getFieldReadOnly = useCallback((name: string) => {
        if (!(name in fieldsState)) { return undefined }

        return fieldsState[name].readOnly
    }, [fieldsState])

    const getFieldSelectionMode = useCallback((name: string) => {
        if (!(name in fieldsState)) { return undefined }
        return fieldsState[name].selectionMode
    }, [fieldsState])

    const addFieldChangeCallback = useCallback((field: string, callback: (value: any) => void, fromSelf=false, from?: string) => {
        callbacksDispatch(addCallback({ field, cbObj: { self: fromSelf, callback: callback, from } }))
    }, [])

    const removeFieldChangeCallback = useCallback((field: string, callback: (value: any) => void, from?: string) => {
        callbacksDispatch(removeCallback({ field, callback }))
    }, [])

    const _onFieldValueNotFound = useCallback((field: string) => {
        onFieldValueNotFound && onFieldValueNotFound(field)
    }, [onFieldValueNotFound])

    const hasCallbacks = useCallback((field: string) => {
        return _.includes(hasCallbacksState, field)
    }, [hasCallbacksState])

    const resetContext = useCallback(() => {
        dispatch(_resetState())
    }, [])

    const lookupContextProps = {
        fields: fieldsState,
        addField,
        removeField,
        setFieldValue,
        getFieldValue,
        getFieldReadOnly,
        getFieldSelectionMode,
        addFieldChangeCallback,
        removeFieldChangeCallback,
        onFieldValueNotFound: _onFieldValueNotFound,
        fieldHasCallbacks: hasCallbacks,
    }

    useEffect(() => {
        const fields = Object.keys(callbacksState)
        setHasCallbacksState((_fields) => { return _.isEqual(_fields.sort(), fields.sort()) ? _fields : fields })
    }, [callbacksState])

    useImperativeHandle(ref, () => ({
        resetContext
    }), [resetContext])

    return (
        <IxLookupContext.Provider value={lookupContextProps}>
            { props.children }
        </IxLookupContext.Provider>
    )
})

const useIxLookupContext = () => useContext(IxLookupContext)

export {
    IxLookupProvider,
    useIxLookupContext,
}
